import React, { useEffect } from "react";
import { Modal, Input, Typography, Button, Space, Form } from "antd";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createRoleSchema } from "../../../../../../validation";
import { MAX_HEADING_LENGTH } from "../../../../../../domain/clinicalForms";

const defaultValues = { name: "", description: "" };

const CreateRoleModal = ({ open, onCancel, onCreate, loading }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(createRoleSchema),
    defaultValues,
    mode: "onTouched",
  });

  useEffect(() => {
    if (!open) reset(defaultValues);
  }, [open, reset]);

  const onSubmit = (values) => onCreate(values);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={520}
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Create New Role
        </Typography.Title>
      }
      styles={{ body: { padding: "8px 4px 4px" } }}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Define a new clinical role for your organization
      </Typography.Paragraph>
      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Role Name</span>}
              required
              validateStatus={errors.name ? "error" : ""}
              help={
                errors.name?.message || (
                  <span style={{ color: "#98a2b3" }}>
                    This role will appear in the left navigation panel and can
                    have unlimited forms.
                  </span>
                )
              }
              style={{ marginBottom: 16 }}
            >
              <Input
                {...field}
                autoFocus
                size="large"
                maxLength={MAX_HEADING_LENGTH}
                showCount
                placeholder="e.g. Cardiologist, Radiologist, Neurologist..."
              />
            </Form.Item>
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Description</span>}
              validateStatus={errors.description ? "error" : ""}
              help={errors.description?.message}
              style={{ marginBottom: 24 }}
            >
              <Input.TextArea
                {...field}
                placeholder="Optional description"
                autoSize={{ minRows: 2, maxRows: 4 }}
              />
            </Form.Item>
          )}
        />

        <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
          <Space size={12}>
            <Button size="large" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              size="large"
              type="primary"
              htmlType="submit"
              loading={loading || isSubmitting}
            >
              Create Role
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateRoleModal;
